import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import {
  UserIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  QrCodeIcon,
  HomeIcon,
  PrinterIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline'

import Table from '../common/Table'
import FiltersComponent from './FiltersComponent'

// Utility to capitalize strings (first letter of each word)
const capitalize = (str) =>
  typeof str === 'string' && str.length > 0
    ? str.split(' ').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ')
    : '';

const ResidentsTable = ({
  residents,
  isLoading = false,
  search,
  onSearchChange,
  onView,
  onEdit,
  onDelete,
  onPrint,
  onCreateFamily,
  onReassignHousehold,
  getPhotoUrl,
  pagination,
  filters,
  setFilters,
  showFilters,
  setShowFilters,
  onPageChange,
}) => {
  const [isFocused, setIsFocused] = useState(false)

  // if (isLoading) {
  //   return (
  //     <div className='bg-white rounded-lg shadow animate-pulse'>
  //       <div className='px-6 py-4 border-b border-gray-200'>
  //         <div className='h-6 bg-gray-300 rounded w-24'></div>
  //       </div>
  //       <div className='p-6'>
  //         <div className='space-y-4'>
  //           {[...Array(5)].map((_, index) => (
  //             <div key={index} className='flex items-center space-x-4'>
  //               <div className='w-12 h-12 bg-gray-300 rounded-full'></div>
  //               <div className='flex-1 space-y-2'>
  //                 <div className='h-4 bg-gray-300 rounded w-3/4'></div>
  //                 <div className='h-3 bg-gray-300 rounded w-1/2'></div>
  //               </div>
  //               <div className='flex space-x-2'>
  //                 <div className='w-8 h-8 bg-gray-300 rounded'></div>
  //                 <div className='w-8 h-8 bg-gray-300 rounded'></div>
  //                 <div className='w-8 h-8 bg-gray-300 rounded'></div>
  //               </div>
  //             </div>
  //           ))}
  //         </div>
  //       </div>
  //     </div>
  //   )
  // }

  const columns = [
    {
      key: 'resident',
      label: 'Resident & ID',
      render: (_, resident) => (
        <div className='flex items-center'>
          <div className='flex-shrink-0 h-12 w-12'>
            {getPhotoUrl(resident) ? (
              <img
                src={getPhotoUrl(resident)}
                alt={`${resident.first_name} ${resident.last_name}`}
                className='h-12 w-12 rounded-full object-cover border-2 border-blue-200 hover:scale-110 transition-transform duration-200'
                onError={(e) => {
                  e.target.style.display = 'none'
                  e.target.parentNode.querySelector(
                    '.fallback-avatar'
                  ).style.display = 'flex'
                }}
              />
            ) : null}
            <div
              className={`fallback-avatar h-12 w-12 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-sm hover:scale-110 transition-transform duration-200 ${
                getPhotoUrl(resident) ? 'hidden' : 'flex'
              }`}
            >
              {resident.first_name.charAt(0)}
              {resident.last_name.charAt(0)}
            </div>
          </div>
          <div className='ml-4 flex-1 min-w-0'>
            <div className='text-sm font-medium text-gray-900 truncate'>
              {capitalize(resident.first_name)} {capitalize(resident.last_name)}
            </div>
            <div className='text-xs text-gray-500 truncate'>
              Born:{' '}
              {resident.birthdate
                ? new Date(resident.birthdate).toLocaleDateString()
                : 'N/A'}
            </div>
            <div className='flex items-center space-x-2 mt-1'>
              <span className='font-mono text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded'>
                {resident.barangay_id_number}
              </span>
              {resident.barangay_id_number && (
                <div className='flex items-center space-x-1'>
                  <QrCodeIcon
                    className='h-3 w-3 text-green-600'
                    title='QR Code Available'
                  />
                  <span className='text-xs text-green-600'>QR</span>
                </div>
              )}
            </div>
            <div className='text-xs text-gray-500 truncate mt-0.5'>
              {resident.occupation || 'N/A'}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'contact',
      label: 'Contact',
      render: (_, resident) => (
        <div className='text-sm text-gray-900'>
          {resident.contact_number && (
            <div className='flex items-center text-xs'>
              <PhoneIcon className='h-3 w-3 text-gray-400 mr-1' />
              <span className='truncate'>{resident.contact_number}</span>
            </div>
          )}
          {resident.email && (
            <div className='flex items-center text-xs mt-1'>
              <EnvelopeIcon className='h-3 w-3 text-gray-400 mr-1' />
              <span className='truncate max-w-xs'>{resident.email}</span>
            </div>
          )}
          {resident.full_address && (
            <div className='flex items-start text-xs mt-1 w-full'>
              <MapPinIcon className='h-3 w-3 text-gray-400 mr-1 mt-0.5 shrink-0' />
              <span className='block w-full max-w-xs line-clamp-2 break-words whitespace-normal'>
                {resident.full_address}
              </span>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'household',
      label: 'Household',
      render: (_, resident) => (
        <div className='text-sm text-gray-900'>
          <div className='flex items-center space-x-1 mb-1'>
            <HomeIcon className='h-3 w-3 text-gray-400' />
            <span className='font-mono text-xs bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded'>
              {resident.household_number || 'N/A'}
            </span>
          </div>
          <div className='text-xs text-gray-500'>
            {resident.is_household_head
              ? `Head • ${resident.household_members?.length || 0} members`
              : 'Member'}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Digital ID',
      render: (_, resident) => (
        <span
          className={`inline-flex px-2 py-1 text-center text-xs font-semibold rounded-full
          ${
            resident.status === 'active'
              ? 'bg-green-100 text-green-800'
              : resident.status === 'verified'
              ? 'bg-blue-100 text-blue-800'
              : resident.status === 'pending'
              ? 'bg-yellow-100 text-yellow-800'
              : 'bg-gray-100 text-gray-800'
          }`}
        >
          {resident.status}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, resident) => (
        <div className='flex items-center justify-end space-x-1'>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onView(resident)
            }}
            className='text-blue-600 hover:text-blue-900 p-1.5 rounded-full hover:bg-blue-50 transition-all duration-200 hover:scale-110'
            title='View Details'
          >
            <EyeIcon className='h-4 w-4' />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onEdit(resident)
            }}
            className='text-yellow-600 hover:text-yellow-900 p-1.5 rounded-full hover:bg-yellow-50 transition-all duration-200 hover:scale-110'
            title='Edit'
          >
            <PencilIcon className='h-4 w-4' />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onPrint(resident)
            }}
            className='text-green-600 hover:text-green-900 p-1.5 rounded-full hover:bg-green-50 transition-all duration-200 hover:scale-110'
            title='Print'
          >
            <PrinterIcon className='h-4 w-4' />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onDelete(resident)
            }}
            className='text-red-600 hover:text-red-900 p-1.5 rounded-full hover:bg-red-50 transition-all duration-200 hover:scale-110'
            title='Delete'
          >
            <TrashIcon className='h-4 w-4' />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              if (onCreateFamily) onCreateFamily(resident)
            }}
            className='text-indigo-600 hover:text-indigo-900 p-1.5 rounded-full hover:bg-indigo-50 transition-all duration-200 hover:scale-110'
            title='Create Family'
          >
            <UserIcon className='h-4 w-4' />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              if (onReassignHousehold) onReassignHousehold(resident)
            }}
            className='text-green-600 hover:text-green-900 p-1.5 rounded-full hover:bg-green-50 transition-all duration-200 hover:scale-110'
            title='Reassign Household'
          >
            <HomeIcon className='h-4 w-4' />
          </button>
        </div>
      ),
    },
  ]

  return (
    <div className='bg-white rounded-md border-t'>
      <div className='flex items-center justify-between px-4 py-4'>
        <h3 className='text-lg font-medium text-gray-900'>Residents</h3>
        <div className='justify-center flex gap-2'>
          <input
            type='text'
            placeholder='Search residents...'
            value={search || ''}
            autoFocus={search ? true : false}
            onFocus={() => setIsFocused(true)}
            onBlur={({ target }) => {
              if (search) {
                target.focus()
              } else {
                setIsFocused(false)
              }
            }}
            onChange={(e) => onSearchChange(e.target.value)}
            className={`w-64 border rounded-lg px-4 py-2 text-sm
    focus:outline-none focus:ring-1 focus:ring-blue-500
    transition-all duration-200
    ${
      search
        ? 'border-blue-500 ring-1 ring-blue-500'
        : 'border-gray-300 bg-white'
    }
    ${!search && !isFocused ? 'caret-transparent' : ''}`}
          />
          <button
            onClick={() => setShowFilters(!showFilters)}
            className='inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 hover:scale-105 active:scale-95'
          >
            <FunnelIcon className='w-4 h-4 mr-2' />
            Filters
          </button>
        </div>
      </div>
      <FiltersComponent
        filters={filters}
        setFilters={setFilters}
        showFilters={showFilters}
      />
      {residents.length === 0 ? (
        <div className='text-center py-12 border-t'>
          <UserIcon className='mx-auto h-12 w-12 text-gray-400' />
          <h3 className='mt-4 text-lg font-medium text-gray-900'>
            No residents found
          </h3>
          <p className='mt-2 text-sm text-gray-500'>
            Try adjusting your search keywords.
          </p>
        </div>
      ) : (
        <Table
          columns={columns}
          data={residents}
          getRowKey={(resident) => resident.id}
          pagination={pagination}
          onPageChange={onPageChange}
          paginationProps={{
            showSizeSelector: true,
            sizeOptions: [5, 10, 15, 25, 50, 100],
          }}
          getRowClassName={(_, index) =>
            'hover:bg-gray-50 transition-all duration-200 animate-in slide-in-from-left group'
          }
          getRowStyle={(_, index) => ({ animationDelay: `${index * 100}ms` })}
        />
      )}
    </div>
  )
}

export default memo(ResidentsTable)
